// A BLOG POST, as an author writes it.
//
// Ported from gen-2 (archive/recovered/stack-pkg/src/data/blog-post/fields),
// with three things gone on purpose. The `type` enum that chose between a
// paragraph and a markdown body is not here — a post is a Quill delta, the same
// thing a `paragraph` element is, and the markdown renderer was never ported.
// The `domain`/`site` pair that gen-2 denormalised beside its key is `orgId`/
// `siteId`, the same pair every row in this service carries. And the id is
// MINTED rather than typed: gen-2's `postid` was a slug the author chose, which
// is why its published copy was keyed by slug and why renaming one orphaned
// the old URL (see ../blog-published). Here `postId` is a uuid nobody sees and
// `slug` is a field, free to change.
//
// Two dates, and they mean different things. `date` is the AUTHOR'S — the date
// a post says it was written, shown on the page, backdatable. `publishedAt` is
// the SERVER'S — the instant Publish was last pressed, null until then. Gen-2
// had only the first and copied it on publish, so nothing ever recorded when a
// post actually went live.
//
// `editedAt` exists because datorm moves `updatedAt` on every write, including
// the publish that stamps `publishedAt`, so "has this draft changed since it
// was published" cannot be read off `updatedAt`. The model sets it on create and
// update and leaves it alone on publish — see ./status.
//
// NO `format:` ON ANY OF THE DATES. datorm validates the stored row with raw
// jsonschema and forgives exactly one format error, `date`; a `date-time` format
// on an empty string would refuse the whole row. They are ISO strings by
// convention, which is what the date widget writes.
import { properties } from "@repoflow/common/schema-fields";
import { siteKey } from "@common/schema/site/key";

const { createdAt, updatedAt } = properties;

export { createdAt, updatedAt };

export { siteKey };

export const orgId = {
  type: "string",
};

export const siteId = {
  type: "string",
};

export const postId = {
  type: "string",
};

// What names the post in a URL. Unique among a site's PUBLISHED posts, checked
// at publish time rather than on every save — a draft has no URL to collide on.
export const slug = {
  label: "Slug",
  $ref: "/Blog/Slug",
  default: "",
};

export const title = {
  label: "Title",
  type: "string",
  maxLength: 256,
  default: "",
};

export const abstract = {
  label: "Abstract",
  type: "string",
  textarea: true,
  default: "",
};

// The same `{url, description}` a page's image param carries, so the file
// picker and the renderer's path rebasing both already know it.
export const image = {
  label: "Image",
  $ref: "/Types/Image",
  default: { url: "", description: "" },
};

export const tags = {
  label: "Tags",
  type: "array",
  items: { type: "string" },
  widget: "tags",
  default: [],
};

// Names one of the site's authors (@common/schema/site/fields `blog.authors`).
// Empty means no author, which is what a post written before the directory has.
export const authorId = {
  label: "Author",
  type: "string",
  default: "",
};

// Nullable on the PUBLIC shape because the date widget's clear control writes
// `null`, and a form whose value fails its own schema greys out Save. The model
// turns that null into "" before the row is written, so the stored shape stays
// a string.
export const date = {
  label: "Date",
  type: ["string", "null"],
  widget: "date",
  default: "",
};

export const storedDate = {
  type: "string",
};

export const content = {
  label: "Content",
  type: "string",
  widget: "paragraph",
  default: `{"ops":[]}`,
};

export const editedAt = {
  label: "Edited at",
  type: "string",
};

// Null until the first publish. Nullable rather than "" because an empty string
// would read as a timestamp to anything sorting on it, and because it is what
// datorm writes for an absent property anyway.
export const publishedAt = {
  label: "Published at",
  type: ["string", "null"],
};

// The slug the published copy is live under, which is not necessarily the
// draft's current slug: an author who renames and does not republish has a
// draft at one address and a page at another, and the screen says so.
export const publishedSlug = {
  label: "Published as",
  type: ["string", "null"],
};

// Derived, never stored — see ./status.
export const status = {
  label: "Status",
  type: "string",
};

export default [
  {
    id: "/Blog/Slug",
    type: "string",
    maxLength: 128,
    pattern: "^[a-z0-9]+(?:-[a-z0-9]+)*$",
  },
];
